import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { userAccountService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { User } from '../types/user';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import UserAccountList from '../components/user-accounts/UserAccountList';
import UserAccountForm from '../components/user-accounts/UserAccountForm';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

function UserAccounts() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingAccount, setEditingAccount] = useState<User | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: accounts, isLoading } = useQuery({
    queryKey: ['user-accounts'],
    queryFn: () => userAccountService.getAccounts(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<User>) => userAccountService.createAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-accounts'] });
      messageHandler.success('Conta criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: User) => userAccountService.updateAccount(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-accounts'] });
      messageHandler.success('Conta atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => userAccountService.deleteAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-accounts'] });
      messageHandler.success('Conta excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const resetTwoFactorMutation = useMutation({
    mutationFn: (id: number) => userAccountService.resetTwoFactor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-accounts'] });
      messageHandler.success('2FA redefinido com sucesso! O usuário poderá configurá-lo novamente no próximo login.');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (data: any) => {
    try {
      const submitData = { ...data };

      if (editingAccount) {
        if (!submitData.password || submitData.password === '') {
          delete submitData.password;
          delete submitData.confirmPassword;
        }
        updateMutation.mutate({ ...editingAccount, ...submitData });
      } else {
        createMutation.mutate(submitData);
      }
    } catch (error: any) {
      messageHandler.error(error);
    }
  };

  const handleDelete = (account: User) => {
    messageHandler.confirm('Excluir Conta', 'Tem certeza que deseja excluir esta conta?', () => {
      deleteMutation.mutate(account.id);
      // Close the confirmation modal
      closeModal();
    });
  };

  const handleResetTwoFactor = (account: User) => {
    messageHandler.confirm(
      'Resetar 2FA',
      `Tem certeza que deseja redefinir a autenticação de dois fatores do usuário "${account.username}"? Ele precisará configurá-la novamente no próximo login.`,
      () => {
        resetTwoFactorMutation.mutate(account.id);
      }
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingAccount(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          <PageHeader title="Usuários" description="Gerencie as contas de usuário vinculadas." />
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Usuários"
          description="Gerencie as contas de usuário vinculadas."
          actions={
            <Button
              onClick={() => {
                setEditingAccount(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Conta
            </Button>
          }
        />

        <UserAccountList
          accounts={accounts || []}
          onEdit={account => {
            setEditingAccount(account);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
        />

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingAccount ? 'Editar Conta' : 'Nova Conta'}
        >
          <UserAccountForm
            key={editingAccount?.id || 'new'}
            account={editingAccount}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
            onResetTwoFactor={handleResetTwoFactor}
            isResettingTwoFactor={resetTwoFactorMutation.isPending}
          />
        </Modal>
      </div>
    </div>
  );
}

export default UserAccounts;
